<template>
	<v-dialog
		v-model="modal"
		:fullscreen="isFullScreen"
		:max-width="
			!isFullScreen
				? `${Math.min(width ? width : dialogWidth, 1200)}px`
				: `${windowSize.width}px`
		"
		:persistent="persistent"
		:scrollable="scrollable"
		:transition="transition"
		:height="height ?? undefined"
		:close-on-back="false"
		:data-test="`${dataTest}-dialog`"
		v-bind="$attrs"
		@keydown.esc.stop="escape"
	>
		<template v-if="$slots.activator" #activator="activatorProps">
			<slot name="activator" v-bind="activatorProps"></slot>
		</template>

		<template #default="defaultProps">
			<slot v-if="loading" name="loading">
				<Loading data-test="dialog" />
			</slot>
			<slot v-else v-bind="defaultProps">
				<v-card data-test="dialog--content-card">
					<slot v-if="$slots.dialogTitle || title" name="dialogTitle">
						<v-card-title
							class="dialog-title d-flex justify-space-between align-center"
						>
							{{ translate(title) }}
							<v-btn icon size="small" @click.stop="close" variant="flat">
								<v-icon>fal fa-times</v-icon>
							</v-btn>
						</v-card-title>
					</slot>
					<v-divider />
					<v-card-text v-if="$slots.dialogText" :class="textClass">
						<slot name="dialogText" />
					</v-card-text>
					<v-divider />
					<v-card-actions v-if="$slots.dialogActions">
						<slot name="dialogActions" />
					</v-card-actions>
				</v-card>
			</slot>
		</template>
	</v-dialog>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useWindowSize } from '@vueuse/core'
import { useRoute } from 'vue-router'
import Loading from 'components/templates/Loading.vue'
import { useRouteHistory } from 'composables/useRouteHistory'
import { useModal } from 'composables/useModal'
import { useTranslate } from 'composables/useTranslate.ts'

type WidthFactorThresholds = {
	xs?: number
	sm?: number
	md?: number
	lg?: number
	xl?: number
}

type WidthFactor = number | WidthFactorThresholds

type DialogProps = {
	closeOnEscape?: boolean
	forceFullscreen?: boolean
	fullscreen?: boolean
	handlePrevRoute?: boolean
	loading?: boolean
	height?: number | null
	scrollable?: boolean
	persistent?: boolean
	title?: string
	toggle: boolean
	transition?: string
	width?: number
	widthFactor?: WidthFactor
	textClass?: string
	dataTest?: string
}

const emit = defineEmits<{
	'update:toggle': [value: boolean]
	'update:fullscreen': [value: boolean]
	dialogClose: [value: boolean]
}>()

const props = withDefaults(defineProps<DialogProps>(), {
	closeOnEscape: false,
	forceFullscreen: false,
	fullscreen: true,
	handlePrevRoute: true,
	loading: false,
	height: 900,
	scrollable: false,
	persistent: true,
	title: undefined,
	transition: 'dialog-bottom-transition',
	width: undefined,
	widthFactor: 0.9,
	textClass: '',
})

const { translate } = useTranslate()

// Custom validator for widthFactor
const validateWidthFactor = (value: WidthFactor): boolean => {
	if (typeof value === 'number') return true

	const validKeys = ['xs', 'sm', 'md', 'lg', 'xl']
	const objectValue = value as WidthFactorThresholds

	return (
		Object.keys(objectValue).every((key) => validKeys.includes(key)) &&
		Object.values(objectValue).every((val) => typeof val === 'number')
	)
}

// Validate widthFactor prop
if (!validateWidthFactor(props.widthFactor)) {
	console.warn('Invalid widthFactor prop provided to Dialog component')
}

const display = useDisplay()
const windowSize = useWindowSize()
const route = useRoute()
const routeHistory = useRouteHistory()
const { closeModal } = useModal()

const reverseShallow = <T,>(param: T[] = []): T[] => {
	return [...param].reverse()
}

const fillMissingThresholds = (
	param: WidthFactorThresholds = {}
): Record<string, number> => {
	const validThresholds = ['xs', 'sm', 'md', 'lg', 'xl']
	const reversedThresholds = reverseShallow(validThresholds)
	const result: Record<string, number> = {}

	// Initialize with provided values or -1
	validThresholds.forEach((threshold) => {
		result[threshold] = param[threshold as keyof WidthFactorThresholds] ?? -1
	})

	// Fill missing values
	validThresholds.forEach((threshold) => {
		if (result[threshold] === -1) {
			// Look for larger threshold value
			const largerThreshold = reversedThresholds
				.slice(reversedThresholds.indexOf(threshold))
				.find((el) => result[el] !== -1)

			if (largerThreshold) {
				result[threshold] = result[largerThreshold]
			} else {
				// Look for smaller threshold value
				const smallerThreshold = validThresholds
					.slice(validThresholds.indexOf(threshold))
					.find((el) => result[el] !== -1)

				if (smallerThreshold) {
					result[threshold] = result[smallerThreshold]
				}
			}
		}
	})

	return result
}

const dialogWidth = computed((): number => {
	const displayName = display.name.value
	const threshold = displayName === 'xl' ? 'lg' : displayName

	const factor =
		typeof props.widthFactor === 'number'
			? props.widthFactor
			: fillMissingThresholds(props.widthFactor as WidthFactorThresholds)[
					displayName
				]

	return display.thresholds.value[threshold] * factor
})

const modal = computed({
	get: (): boolean => props.toggle,
	set: (value: boolean): void => {
		emit('update:toggle', value)
	},
})

const isFullScreen = computed({
	get: (): boolean => {
		return props.fullscreen || props.forceFullscreen
			? props.forceFullscreen || display.smAndDown.value
			: false
	},
	set: (value: boolean): void => {
		emit('update:fullscreen', value)
	},
})

watch(
	() => props.toggle,
	(current: boolean, previous: boolean) => {
		if (!current && previous) {
			if (route.query.modal && props.handlePrevRoute) {
				closeModal(route.query.modal as string)
				return
			}
			if (props.handlePrevRoute) {
				routeHistory.navigateToHistoryRoute()
			}
		}
	}
)

const close = (): void => {
	emit('dialogClose', true)
	modal.value = false
}

const escape = (): void => {
	if (!props.closeOnEscape) {
		return
	}
	close()
}

defineExpose({
	close,
	escape,
})
</script>

<style scoped>
/* Der Dialogkopf steht auf derselben Stufe wie eine Tabellen-Kopfzeile. Nicht
   aus Dekoration: beide tragen Struktur statt Inhalt, und wenn sie dieselbe
   Flaeche tragen, muss man die Regel nur einmal lernen. Bisher lief der Kopf
   in derselben Farbe wie der Inhalt darunter und war allein durch die
   Trennlinie vom Text unterschieden.

   Bewusst hier und nicht als globale `.v-overlay .v-card-title`-Regel: 75
   Dialoge laufen ueber diese Komponente, aber der Handover-Stepper baut seinen
   Kopf selbst (Vue2-Altbestand) und wuerde von einer globalen Regel
   mitgetroffen. */
.dialog-title {
	background-color: var(--tenant-surface-2);
}
</style>
